/**
 * `assetCtxs` WS sync — keeps the market-meta cache (mark/oracle/funding/OI/
 * volume) real-time for ALL situations: logged-out, xyz/HIP-3, and main DEX.
 *
 * Why this exists:
 *   The `["trade","meta-and-asset-contexts"]` cache previously only updated via
 *   `webData2` (main DEX + logged-in only) or a 60s REST poll. So mark/oracle
 *   sat stale for logged-out users and on every xyz market. `assetCtxs({ dex })`
 *   is a public, login-free WS topic carrying the full per-DEX context array —
 *   exactly the fields market-info renders. Two subs (main + xyz) cover every
 *   case and, as a bonus, the trade-page market list updates live too.
 *
 * Mounted from `trade-layout.tsx` (trade-page-scoped, like l2Book/trades subs).
 * The REST poll in `useTradeMetaQuery` stays as the universe bootstrap + backstop;
 * these subs only swap the contexts array, preserving the polled `meta.universe`.
 */
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "#/hooks/query-keys";
import { XYZ_DEX_NAME } from "../lib/hyperliquid";
import { useHyperliquidSubscription } from "./use-hyperliquid-subscription";
import {
	type CombinedMetaResponse,
	type MetaAndAssetCtxsResponse,
	setXyzMetaFallback,
} from "./use-trade-market";

type AssetCtxs = MetaAndAssetCtxsResponse[1];

/**
 * Swap one DEX slot's contexts array with a fresh WS push, preserving the
 * REST-sourced `meta.universe`. Pure so it's unit-tested without React.
 */
export function patchMetaCtxs(
	old: CombinedMetaResponse | undefined,
	slot: "main" | "xyz",
	ctxs: AssetCtxs,
): CombinedMetaResponse | undefined {
	// Wait for the REST poll to provide `meta.universe` first (mirrors webData2's
	// `if (!old)` guard). xyz slot may still be null until its REST fetch lands.
	if (!old) return old;
	const existing = old[slot];
	if (!existing) return old;

	const [meta] = existing;
	// ponytail: length guard for index alignment — `assetCtxs` ctxs order tracks
	// `meta.universe` index (HL's contract; webData2 pairs them the same way). A
	// mismatch (e.g. a fresh listing) self-heals on the next REST poll.
	if (ctxs.length !== meta.universe.length) return old;

	const next: MetaAndAssetCtxsResponse = [meta, ctxs];
	if (slot === "xyz") setXyzMetaFallback(next);
	return { ...old, [slot]: next };
}

export function useTradeMetaSync() {
	const queryClient = useQueryClient();

	const patch = (slot: "main" | "xyz", ctxs: AssetCtxs) => {
		queryClient.setQueryData<CombinedMetaResponse>(
			queryKeys.trade.metaAndAssetContexts(),
			(old) => patchMetaCtxs(old, slot, ctxs),
		);
	};

	// ponytail: no login gate — a public topic, and since webData2's removal
	// these two subs are the ONLY real-time source for the contexts array
	// (logged-in or not).
	useHyperliquidSubscription({
		deps: [],
		subscribe: (client) =>
			client.assetCtxs({ dex: "" }, (data) =>
				patch("main", data.ctxs as AssetCtxs),
			),
	});

	useHyperliquidSubscription({
		deps: [],
		subscribe: (client) =>
			client.assetCtxs({ dex: XYZ_DEX_NAME }, (data) =>
				patch("xyz", data.ctxs as AssetCtxs),
			),
	});
}
